<template>
  <div class="badge-stack">
    <v-card :class="['rounded-circle', glowClass]" :to="`/curso/${curso.id}`" :height="size" :width="size">
      <v-img :src="curso.imagen_url" class="align-end overlay-container" :gradient="imgGradient" aspect-ratio="1" cover>
        <!-- Tick (nota ≥ 5) centrado -->
        <v-icon v-if="showTick" icon="mdi-check-circle" class="tick-overlay" :style="{
          color: tickColor,
          fontSize: tickSize + 'px'
        }" />
        <!-- Título dentro SOLO si no es petit -->
        <v-card-text v-if="!petit" class="text-white text-center text-h6 card-title">
          {{ curso.titulo }}
        </v-card-text>
      </v-img>
    </v-card>
    <!-- Título debajo cuando es petit -->
    <div v-if="petit" class="titulo-bajo text-center mt-2" :style="{ width: size + 'px' }">
      {{ curso.titulo }}
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

// Props
const { curso, petit } = defineProps({
  curso: { type: Object, default: () => ({ notas: {} }) },
  petit: { type: Boolean, default: false },
})

// Tamaños en función de 'petit'
const size = computed(() => (petit ? 100 : 200))     // círculo más pequeño en modo petit
const tickSize = computed(() => (petit ? 70 : 120))  // ajusta el icono del tick en modo petit

// Mapa de colores y gradientes por rango
const COLOR_MAP = {
  oro: 'rgba(255,215,0,0.6)',
  plata: 'rgba(192,192,192,0.6)',
  bronce: 'rgba(205,127,50,0.6)',
  ninguno: 'rgba(255,255,255,0.6)'
}

const GRADIENT_MAP = {
  oro: 'to bottom, rgba(255,215,0,0.25), rgba(0,0,0,.7)',
  plata: 'to bottom, rgba(192,192,192,0.25), rgba(0,0,0,.7)',
  bronce: 'to bottom, rgba(205,127,50,0.25), rgba(0,0,0,.7)',
  ninguno: 'to bottom, rgba(0,0,0,0), rgba(0,0,0,.7)'
}

// Nota final (puede ser undefined)
const nota = computed(() => curso?.notas?.nota_final)

// Rango según la nota
const rango = computed(() => {
  const n = nota.value
  if (n == null) return 'ninguno'
  if (n >= 9) return 'oro'
  if (n >= 8) return 'plata'
  if (n >= 7) return 'bronce'
  return 'ninguno'
})

// Mostrar tick si nota ≥ 5
const showTick = computed(() => nota.value >= 5)

// Color y gradiente basados en el rango
const tickColor = computed(() => COLOR_MAP[rango.value])
const imgGradient = computed(() => GRADIENT_MAP[rango.value])

// Clase de glow (sólo si rango ≠ ninguno)
const glowClass = computed(() =>
  rango.value === 'ninguno' ? '' : `glow-pulse ${rango.value}`
)
</script>

<style scoped>
.badge-stack{
  display: flex;
  flex-direction: column; /* << clave */
  align-items: center;
}

/* Glow pulse reutilizando --glow */
.glow-pulse {
  --glow: rgba(0, 0, 0, 0);
  box-shadow: 0 0 20px var(--glow);
}

/* Definición de colores de glow por rango */
.oro {
  --glow: rgba(255, 215, 0, .8);
}

.plata {
  --glow: rgba(192, 192, 192, .8);
}

.bronce {
  --glow: rgba(205, 127, 50, .8);
}

/* Overlay y z-index */
.overlay-container {
  position: relative;
}

.tick-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
}

.card-title {
  position: relative;
  z-index: 2;
}

/* Título pequeño debajo en modo 'petit' */
.titulo-bajo {
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
